'use client';

import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import Modal from './Modal';
import PhraseGrid from './PhraseGrid';
import { ZineButton } from './ui/ZineButton';
import { ItalicBody } from './ui/ItalicBody';
import { syncClient } from '@/lib/syncClient';
import { useI18n } from '@/lib/i18n';

/**
 * Pop-up that reveals the user's 12-word recovery phrase directly.
 *
 * Used by the Settings "Data safety" section: tapping "Show my
 * recovery phrase" opens this, instead of scrolling down to the Sync
 * panel and making the user hunt for a second button (Sebastian,
 * Telegram 2026-05-22 — "the click should really show the phrase, not
 * only roll me down to the other button").
 *
 * Reads the phrase straight from `syncClient.getPhrase()` when open.
 * Renders nothing if there is no enrolled phrase — the caller only
 * opens it for enrolled users.
 */
export default function RecoveryPhraseModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  if (!open) return null;
  const phrase = syncClient.getPhrase();
  if (!phrase) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(phrase);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — the words are on screen to copy by hand */
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t.syncBtnShowPhrase}>
      <div className="space-y-3">
        <ItalicBody size="sm">{t.syncShowPhraseHint}</ItalicBody>
        <PhraseGrid phrase={phrase} />
        <div className="flex gap-2">
          <ZineButton
            variant="outlined"
            size="small"
            onClick={copy}
            data-testid="recovery-phrase-modal-copy"
            style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            {copied ? <Check size={12} aria-hidden /> : <Copy size={12} aria-hidden />}
            {copied ? t.syncCopied : t.syncCopy}
          </ZineButton>
          <ZineButton
            variant="solid"
            size="small"
            onClick={onClose}
            data-testid="recovery-phrase-modal-done"
            style={{ flex: 1 }}
          >
            {t.syncDone}
          </ZineButton>
        </div>
      </div>
    </Modal>
  );
}
